import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  Avatar,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Pill,
  Select,
  Skeleton,
  Textarea,
  SkeletonText,
  Tabs,
  useToast,
} from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Task } from "../lib/api.js";
import { ExportMenu } from "../components/ExportMenu.js";
import { ArchivedBanner, ProjectLifecycleActions } from "../components/Lifecycle.js";
import { InfoPanel } from "../components/InfoPanel.js";
import type { InfoPanelMember } from "../components/InfoPanel.js";
import { ImportTasksDialog } from "../components/ImportTasksDialog.js";
import { NewTaskDialog } from "../components/NewTaskDialog.js";
import { ProjectMilestones } from "../components/ProjectMilestones.js";
import { ProjectGoals } from "../components/ProjectGoals.js";
import { ProjectFieldsTab } from "../components/ProjectFieldsTab.js";
import { ProjectFormsTab } from "../components/ProjectFormsTab.js";
import { ProjectViews } from "../components/task-views/ProjectViews.js";
import { ProjectDocsTab } from "../components/ProjectDocsTab.js";
import { ProjectAutomationsTab } from "../components/ProjectAutomationsTab.js";
import { ProjectLogTab } from "../components/ProjectLogTab.js";
import { ProjectStatusTab } from "../components/ProjectStatusTab.js";
import { ProjectBudgetTab } from "../components/ProjectBudgetTab.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./project-detail.css";

/**
 * A project's home.
 *
 * The Work list answers "what needs doing"; this answers "what is this, who is
 * on it, and what have we produced". Those are different questions, which is
 * why filtering the task list was never a substitute for this page — members
 * and files had API support and nowhere to live.
 */

type ProjectDetailData = {
  project: {
    id: string;
    code: string;
    name: string;
    summary: string | null;
    status: string;
    health: "on_track" | "at_risk" | "off_track";
    ownerId: string;
    ownerName: string;
    startDate: string | null;
    targetDate: string | null;
    version: number;
    isMember: boolean;
    memberRole: string | null;
    canManage: boolean;
    /** False for a read-only viewer; absent on older servers (treated as editable). */
    canEditTasks?: boolean;
    canDelete: boolean;
    /** Archived projects are read-only; canArchive is owner or admin. */
    archivedAt?: string | null;
    canArchive?: boolean;
    /** Owner, project manager or office oversight; absent on older servers. */
    canShare?: boolean;
    createdAt: string;
  };
  members: InfoPanelMember[];
  taskCounts: Record<string, number>;
  /** Unresolved high-severity risks and issues from the project's logs. */
  logSummary?: { openHighRisks: number; openIssues: number };
  /** Computed from the tasks, beside the manual health; null when the project is not active. */
  suggestedHealth?: { rag: "green" | "amber" | "red"; reasons: string[]; summary: string } | null;
};

type ProjectTab = "overview" | "status" | "views" | "fields" | "forms" | "logs" | "docs" | "budget" | "automations";

const RAG_TONE: Record<string, PillTone> = { green: "ok", amber: "warn", red: "danger" };

const HEALTH_TONE: Record<string, PillTone> = {
  on_track: "ok",
  at_risk: "warn",
  off_track: "danger",
};

const HEALTH_LABEL: Record<string, string> = {
  on_track: "On track",
  at_risk: "At risk",
  off_track: "Off track",
};

const STATUS_LABEL: Record<string, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "In review",
  done: "Done",
  cancelled: "Cancelled",
};

const PRIORITY_TONE: Record<string, PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Days remaining, phrased the way someone would say it out loud. */
function describeTarget(iso: string | null): { text: string; overdue: boolean } | null {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { text: `${Math.abs(days)} days overdue`, overdue: true };
  if (days === 0) return { text: "Due today", overdue: true };
  if (days === 1) return { text: "1 day left", overdue: false };
  if (days < 31) return { text: `${days} days left`, overdue: false };
  return { text: `${Math.round(days / 30)} months left`, overdue: false };
}

/* ─────────────────────────────── edit project ─────────────────────────────── */

interface EditProjectProps {
  project: ProjectDetailData["project"];
  members: InfoPanelMember[];
  onSaved: () => void;
  onClose: () => void;
}

/**
 * Editing a project after it exists.
 *
 * The code is deliberately absent: it is referenced in conversation and in
 * links, and renaming it silently breaks both. Everything else about a
 * project is expected to drift as the work does.
 */
function EditProjectDialog({ project, members, onSaved, onClose }: EditProjectProps) {
  const { toast } = useToast();
  const [name, setName] = useState(project.name);
  const [summary, setSummary] = useState(project.summary ?? "");
  const [health, setHealth] = useState(project.health);
  const [status, setStatus] = useState(project.status);
  const [ownerId, setOwnerId] = useState(project.ownerId);
  // Dates arrive as ISO timestamps; <input type="date"> wants the calendar part.
  const [startDate, setStartDate] = useState(project.startDate?.slice(0, 10) ?? "");
  const [targetDate, setTargetDate] = useState(project.targetDate?.slice(0, 10) ?? "");
  const [saving, setSaving] = useState(false);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);

    api
      .patch(`/projects/${project.id}`, {
        name: trimmed,
        summary: summary.trim() || null,
        health,
        status,
        ownerId,
        // Midnight UTC: a calendar date carries no timezone and must not
        // acquire one on the way to the server.
        startDate: startDate ? `${startDate}T00:00:00.000Z` : null,
        targetDate: targetDate ? `${targetDate}T00:00:00.000Z` : null,
        // Refuses the write if someone else changed the project meanwhile,
        // rather than silently overwriting them.
        version: project.version,
      })
      .then(() => {
        onSaved();
        onClose();
        toast("Project updated.");
      })
      .catch((error: unknown) => {
        const message =
          error instanceof ApiError && error.isConflict
            ? "Someone else changed this project. Reload and try again."
            : error instanceof ApiError
              ? error.message
              : "Could not save those changes.";
        toast(message, { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [name, summary, health, status, ownerId, startDate, targetDate, project.id, project.version, onSaved, onClose, toast]);

  return (
    <form
      className="project-edit"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />

      <Textarea
        label="Summary"
        hint="What this project is for, in a sentence or two."
        value={summary}
        maxRows={4}
        onChange={(e) => setSummary(e.target.value)}
      />

      <div className="project-edit__row">
        <Select label="Health" value={health} onChange={(e) => setHealth(e.target.value as typeof health)}>
          <option value="on_track">On track</option>
          <option value="at_risk">At risk</option>
          <option value="off_track">Off track</option>
        </Select>

        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="planning">Planning</option>
          <option value="active">Active</option>
          <option value="on_hold">On hold</option>
          <option value="complete">Complete</option>
        </Select>
      </div>

      <Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
        {/* Only members can own it — the server rejects anyone else, and
            offering a name it will refuse is a promise the UI cannot keep. */}
        {members.map((member) => (
          <option key={member.id} value={member.id}>
            {member.displayName}
          </option>
        ))}
      </Select>

      <div className="project-edit__row">
        <Input label="Start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <Input label="Target date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
      </div>

      <div className="project-edit__actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!name.trim() || saving}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

/* ─────────────────────────────── add member ─────────────────────────────── */

interface AddMemberProps {
  projectId: string;
  existing: InfoPanelMember[];
  onAdded: () => void;
  onClose: () => void;
}

function AddMemberDialog({ projectId, existing, onAdded, onClose }: AddMemberProps) {
  const { toast } = useToast();
  const [people, setPeople] = useState<{ id: string; displayName: string; jobTitle: string | null }[]>([]);
  const [selected, setSelected] = useState("");
  const [role, setRole] = useState("member");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ people: { id: string; displayName: string; jobTitle: string | null }[] }>("/people")
      .then((res) => setPeople(res.people))
      .catch(() => toast("Could not load the directory.", { tone: "danger" }));
  }, [toast]);

  // Someone already on the project is not a candidate; offering them produces
  // a confusing no-op rather than an error.
  const candidates = useMemo(() => {
    const alreadyIn = new Set(existing.map((m) => m.id));
    return people.filter((p) => !alreadyIn.has(p.id));
  }, [people, existing]);

  const submit = useCallback(() => {
    if (!selected) return;
    setSaving(true);
    api
      .post(`/projects/${projectId}/members`, { userId: selected, role })
      .then(() => {
        onAdded();
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not add that person.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [projectId, selected, role, onAdded, onClose, toast]);

  return (
    <form
      className="project-detail__add-member"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {candidates.length === 0 ? (
        <p className="project-detail__muted">Everyone in the directory is already on this project.</p>
      ) : (
        <>
          <Select label="Person" value={selected} onChange={(e) => setSelected(e.target.value)} required>
            <option value="">Choose someone…</option>
            {candidates.map((person) => (
              <option key={person.id} value={person.id}>
                {person.displayName}
                {person.jobTitle ? ` · ${person.jobTitle}` : ""}
              </option>
            ))}
          </Select>

          <Select
            label="Role"
            hint="Managers and editors can change the project itself; members work on its tasks."
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="member">Member</option>
            <option value="editor">Editor</option>
            <option value="manager">Manager</option>
            <option value="viewer">Viewer</option>
          </Select>
        </>
      )}

      <div className="project-detail__dialog-actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!selected}>
          Add to project
        </Button>
      </div>
    </form>
  );
}

/* ─────────────────────────────── main view ─────────────────────────────── */

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [data, setData] = useState<ProjectDetailData | undefined>(undefined);
  usePageTitle(data?.project.name ?? "");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Open by default only where the panel docks beside the page. Below 1100px
  // it is a modal sheet, and opening that on load covers the project.
  const [panelOpen, setPanelOpen] = useState(
    () => !window.matchMedia?.("(max-width: 1100px)").matches,
  );
  const [addingMember, setAddingMember] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The tab is in the URL so a link to the Views tab (and a saved view) opens on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: ProjectTab =
    tabParam === "status" ||
    tabParam === "views" ||
    tabParam === "fields" ||
    tabParam === "forms" ||
    tabParam === "logs" ||
    tabParam === "docs" ||
    tabParam === "budget" ||
    tabParam === "automations"
      ? tabParam
      : "overview";
  const setTab = (next: ProjectTab) =>
    setSearchParams(next === "overview" ? {} : { tab: next }, { replace: true });

  // The log counts refresh without the page-level skeleton, so the tab keeps its place.
  const [logOverride, setLogOverride] = useState<ProjectDetailData["logSummary"]>();
  const refreshLogSummary = useCallback(() => {
    if (!projectId) return;
    api
      .get<ProjectDetailData>(`/projects/${projectId}`)
      .then((res) => setLogOverride(res.logSummary))
      .catch(() => undefined);
  }, [projectId]);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    setNotFound(false);

    Promise.all([
      api.get<ProjectDetailData>(`/projects/${projectId}`),
      api.get<{ tasks: Task[] }>(`/tasks?projectId=${projectId}&limit=200`),
    ])
      .then(([detail, taskList]) => {
        setData(detail);
        setLogOverride(undefined);
        setTasks(taskList.tasks);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) {
          setNotFound(true);
          return;
        }
        toast(error instanceof ApiError ? error.message : "Could not load this project.", { tone: "danger" });
      })
      .finally(() => setLoading(false));
  }, [projectId, toast]);

  useEffect(() => load(), [load]);

  const remove = useCallback(() => {
    if (!projectId) return;
    setDeleting(true);
    api
      .delete(`/projects/${projectId}`)
      .then(() => {
        toast("Project deleted.");
        navigate("/work", { replace: true });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not delete this project.", { tone: "danger" });
        setDeleting(false);
        setDeleteOpen(false);
      });
  }, [projectId, navigate, toast]);

  const logSummary = logOverride ?? data?.logSummary;

  const openTasks = useMemo(
    () => tasks.filter((t) => t.status !== "done" && t.status !== "cancelled"),
    [tasks],
  );

  if (notFound) {
    return (
      <div className="project-detail__missing">
        <EmptyState
          icon="work"
          title="Project not found"
          description="It may have been deleted, or you may not have access to it."
          action={<Button onClick={() => navigate("/work")}>Back to Work</Button>}
        />
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="project-detail">
        <div className="project-detail__main">
          <div className="project-detail__header">
            <Skeleton height={28} width={280} />
          </div>
          <div className="project-detail__body">
            <SkeletonText lines={6} />
          </div>
        </div>
      </div>
    );
  }

  const { members, taskCounts } = data;
  // An archived project is read-only; the server refuses writes with PROJECT_ARCHIVED.
  const project = { ...data.project, canEditTasks: data.project.canEditTasks !== false && !data.project.archivedAt };
  const target = describeTarget(project.targetDate);
  const done = taskCounts.done ?? 0;
  const total = Object.entries(taskCounts)
    .filter(([status]) => status !== "cancelled")
    .reduce((sum, [, count]) => sum + count, 0);
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="project-detail">
      <div className="project-detail__main">
        <PageHeader
          className="project-detail__header"
          /* The code stays ahead of the name, as a prefix rather than a
             suffix — "ONBRD Customer Onboarding" is how people refer to it. */
          leading={
            <>
              <Link to="/work" className="project-detail__back" aria-label="Back to Work">
                <Icon name="chevron-left" size={18} />
              </Link>
              <span className="project-detail__code">{project.code}</span>
            </>
          }
          title={project.name}
          meta={
            <>
              <Pill tone={HEALTH_TONE[project.health] ?? "neutral"} dot>
                {HEALTH_LABEL[project.health] ?? project.health}
              </Pill>
              {data.suggestedHealth && (
                /* What the tasks say, next to what the owner says: a gap between
                   the two is the conversation worth having. */
                <span className="project-detail__suggested" title={data.suggestedHealth.reasons.join("\n")}>
                  <Pill tone={RAG_TONE[data.suggestedHealth.rag] ?? "neutral"}>
                    Suggested: {data.suggestedHealth.summary}
                  </Pill>
                </span>
              )}
              {logSummary && logSummary.openHighRisks > 0 && (
                <Pill tone="danger">
                  {logSummary.openHighRisks} high {logSummary.openHighRisks === 1 ? "risk" : "risks"}
                </Pill>
              )}
              {logSummary && logSummary.openIssues > 0 && (
                <Pill tone="warn">
                  {logSummary.openIssues} open {logSummary.openIssues === 1 ? "issue" : "issues"}
                </Pill>
              )}
            </>
          }
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="work" size={14} />}
                onClick={() => navigate(`/work?project=${project.id}`)}
              >
                Open board
              </Button>

              <ExportMenu kind={`${project.code} tasks`} excelPath={`/exports/tasks.xlsx?projectId=${project.id}`} />

              {project.canEditTasks && (
                <Button variant="ghost" size="sm" onClick={() => setImportOpen(true)}>
                  Import tasks
                </Button>
              )}

              {project.canManage && (
                <IconButton
                  icon={<Icon name="edit" size={15} />}
                  label="Edit project"
                  onClick={() => setEditOpen(true)}
                />
              )}

              <IconButton
                icon={<Icon name="info" size={16} />}
                label={panelOpen ? "Hide details" : "Show details"}
                variant={panelOpen ? "soft" : "ghost"}
                onClick={() => setPanelOpen((v) => !v)}
              />

              <ProjectLifecycleActions
                projectId={project.id}
                projectName={project.name}
                archived={Boolean(project.archivedAt)}
                canManage={project.canManage}
                canArchive={project.canArchive === true}
                onChanged={load}
              />

              {project.canDelete && (
                <IconButton
                  icon={<Icon name="trash" size={15} />}
                  label="Delete project"
                  onClick={() => setDeleteOpen(true)}
                />
              )}
            </>
          }
        />

        {project.archivedAt && <ArchivedBanner archivedAt={project.archivedAt} />}

        <div className="project-detail__tabs">
          <Tabs
            tabs={[
              { id: "overview", label: "Overview" },
              { id: "views", label: "Views" },
              { id: "status", label: "Status" },
              { id: "fields", label: "Fields" },
              { id: "forms", label: "Forms" },
              { id: "logs", label: "Logs" },
              { id: "docs", label: "Docs" },
              { id: "budget", label: "Budget" },
              ...(project.canManage ? [{ id: "automations", label: "Automations" }] : []),
            ]}
            value={tab}
            onChange={(id) => setTab(id as ProjectTab)}
          />
        </div>

        {tab === "views" && (
          <ProjectViews
            projectId={project.id}
            canEdit={project.canEditTasks !== false}
            canShare={project.canEditTasks !== false}
          />
        )}

        {tab === "fields" && (
          <div className="project-detail__body">
            <ProjectFieldsTab projectId={project.id} canEdit={project.canManage} />
          </div>
        )}

        {tab === "forms" && (
          <div className="project-detail__body">
            <ProjectFormsTab projectId={project.id} members={members} canEdit={project.canManage} />
          </div>
        )}

        {tab === "logs" && (
          <div className="project-detail__body">
            <ProjectLogTab projectId={project.id} members={members} canEdit={project.canManage} onChanged={refreshLogSummary} />
          </div>
        )}

        {tab === "budget" && (
          <div className="project-detail__body">
            <ProjectBudgetTab projectId={project.id} />
          </div>
        )}

        {tab === "docs" && (
          <div className="project-detail__body">
            <ProjectDocsTab
              projectId={project.id}
              members={members}
              canEdit={project.canEditTasks !== false && !project.archivedAt}
            />
          </div>
        )}

        {tab === "automations" && project.canManage && (
          <div className="project-detail__body">
            <ProjectAutomationsTab projectId={project.id} members={members} canEdit={!project.archivedAt} />
          </div>
        )}

        {tab === "status" && (
          <div className="project-detail__body">
            <ProjectStatusTab
              projectId={project.id}
              projectCode={project.code}
              canPost={project.canManage}
              canShare={project.canShare === true}
              onPosted={load}
            />
          </div>
        )}

        <div className="project-detail__body" hidden={tab !== "overview"}>
          {project.summary && <p className="project-detail__summary">{project.summary}</p>}

          {/* Progress reads from live task counts rather than the stored
              column, for the same reason Insights does: nothing maintains
              that column, so it reports zero for a project that is nearly
              finished. */}
          <section className="project-detail__stats">
            <div className="project-detail__stat">
              <span className="project-detail__stat-value">{openTasks.length}</span>
              <span className="project-detail__stat-label">Open</span>
            </div>
            <div className="project-detail__stat">
              <span className="project-detail__stat-value">{done}</span>
              <span className="project-detail__stat-label">Done</span>
            </div>
            <div className="project-detail__stat">
              <span className="project-detail__stat-value">{progress}%</span>
              <span className="project-detail__stat-label">Complete</span>
            </div>
            {target && (
              <div className={`project-detail__stat ${target.overdue ? "is-overdue" : ""}`}>
                <span className="project-detail__stat-value">{formatDate(project.targetDate)}</span>
                <span className="project-detail__stat-label">{target.text}</span>
              </div>
            )}
          </section>

          <div
            className="project-detail__progress"
            role="progressbar"
            aria-label="Project progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <span style={{ width: `${progress}%` }} />
          </div>

          <ProjectMilestones projectId={project.id} members={members} canEdit={project.canEditTasks !== false} />
          <ProjectGoals projectId={project.id} />

          <section className="project-detail__section">
            <div className="project-detail__section-head">
              <h2>Open tasks</h2>
              <div className="project-detail__section-actions">
                {/* Icon via the prop, not as a child: children land inside
                    .ui-btn__label, which is display:block, so an inline SVG
                    would wrap onto its own line and overflow the button. */}
                {project.canEditTasks !== false && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Icon name="plus" size={13} />}
                    onClick={() => setCreatingTask(true)}
                  >
                    New task
                  </Button>
                )}
                <Link to={`/work?project=${project.id}`} className="project-detail__see-all">
                  See all {tasks.length}
                  <Icon name="arrow-right" size={13} />
                </Link>
              </div>
            </div>

            {openTasks.length === 0 ? (
              <EmptyState
                icon="check"
                title={tasks.length === 0 ? "No tasks yet" : "Nothing open"}
                description={
                  tasks.length === 0
                    ? "Create the first task and hand it to someone with a brief."
                    : "Every task in this project is done or cancelled."
                }
                action={
                  project.canEditTasks !== false ? (
                    <Button size="sm" onClick={() => setCreatingTask(true)}>
                      New task
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <ul className="project-detail__tasks">
                {openTasks.slice(0, 12).map((task) => (
                  <li key={task.id}>
                    <Link to={`/work/task/${task.id}`} className="project-detail__task">
                      <span className="project-detail__task-title" title={task.title}>
                        {task.title}
                      </span>
                      <Pill tone="neutral">{STATUS_LABEL[task.status] ?? task.status}</Pill>
                      <Pill tone={PRIORITY_TONE[task.priority] ?? "neutral"}>{task.priority}</Pill>
                      {task.assigneeName ? (
                        <Avatar name={task.assigneeName} src={task.assigneeAvatarUrl ?? undefined} size={22} />
                      ) : (
                        <span className="project-detail__unassigned" aria-label="Unassigned">
                          <Icon name="people" size={13} />
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <InfoPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title="Project details"
        about={project.summary}
        members={members}
        fileScope={{ projectId: project.id }}
        canUpload={project.isMember || project.canManage}
        canManageMembers={project.canManage}
        onManageMembers={() => setAddingMember(true)}
        ownerId={project.ownerId}
        onRemoveMember={(member) => {
          api
            .delete(`/projects/${project.id}/members/${member.id}`)
            .then(() => {
              load();
              toast(`${member.displayName} removed from this project.`);
            })
            .catch((error: unknown) => {
              toast(error instanceof ApiError ? error.message : "Could not remove that member.", { tone: "danger" });
            });
        }}
        links={[{ label: "Open in Work", to: `/work?project=${project.id}`, icon: "work" }]}
      >
        <section className="info-panel__section">
          <h4>Overview</h4>
          <dl className="project-detail__meta">
            <div>
              <dt>Owner</dt>
              <dd>{project.ownerName}</dd>
            </div>
            <div>
              <dt>Starts</dt>
              <dd>{formatDate(project.startDate)}</dd>
            </div>
            <div>
              <dt>Target</dt>
              <dd className={target?.overdue ? "is-overdue" : undefined}>{formatDate(project.targetDate)}</dd>
            </div>
            <div>
              <dt>Your role</dt>
              <dd>{project.memberRole ?? "Not a member"}</dd>
            </div>
          </dl>
        </section>
      </InfoPanel>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit project">
        <EditProjectDialog
          project={project}
          members={members}
          onSaved={load}
          onClose={() => setEditOpen(false)}
        />
      </Modal>

      <Modal open={addingMember} onClose={() => setAddingMember(false)} title="Add a member">
        <AddMemberDialog
          projectId={project.id}
          existing={members}
          onAdded={load}
          onClose={() => setAddingMember(false)}
        />
      </Modal>

      {/* Refetches rather than appending locally: the progress bar and the
          stat row read from server-side taskCounts, which a local push would
          leave behind. */}
      <ImportTasksDialog open={importOpen} onClose={() => setImportOpen(false)} projectId={project.id} onChanged={load} />

      <NewTaskDialog
        open={creatingTask}
        onClose={() => setCreatingTask(false)}
        projectId={project.id}
        onCreated={load}
      />

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this project?">
        <p className="project-detail__muted">
          <strong>{project.name}</strong> will be removed. Its {tasks.length} task
          {tasks.length === 1 ? "" : "s"} are kept and become unassigned to any project, so nothing is lost —
          but this cannot be undone.
        </p>
        <div className="project-detail__dialog-actions">
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={remove}>
            Delete project
          </Button>
        </div>
      </Modal>
    </div>
  );
}
